import i18n from '@web/plugins/i18n'

import { ref, computed, watch } from 'vue'
import Analytics from '@web/services/Analytics/Analytics'

import { useStore } from '@web/store'
import { User } from '@web/store/types/modules/user'
import { getState } from '@web/common/ExternalConfig/ExternalConfig'
import { specialProjectId, superSubscriptionsIds } from '@web/common/ExternalConfig/getters'
import sentry, { sentryConfigure } from '@web/common/sentry'
import Logger from '@web/common/Logger'
import { showWarningSubscription } from '@web/common/subscription'
import * as AnalyticEvents from '@web/services/Analytics/events'
import { useYandexMetrika, updateYandexMerikaByConfig } from 'yandex-metrika-vue3'
import { migrateLegacyContinueTrainingKey } from '@web/consts/ContinueTraining'

/*
* @param object ctx
*/
function useApp ({ router }) {
  const store = useStore()

  const loading = ref(true)
  const status = ref('')
  const yandexMetrika = useYandexMetrika()

  const isTake = computed(() => specialProjectId() === 'take')
  const theme = computed(() => store.state.assets.theme)
  const title = computed(() => store.state.assets.title)
  const isLoadedStreams = computed(() => store.getters['stream/isLoadedAll'])
  const user = computed(() => store.getters['user/user'])
  const fbqId = computed<string | undefined>(() => getState()?.data?.fbq)
  const isLoadedSubscriptions = computed(() => store.getters['subscription/isLoaded'])
  const hasSubscription = computed(() => store.getters['subscription/hasSubscription'])
  const streams = computed(() => store.getters['stream/streams'])
  const isShowNewLk = computed(() => store.getters['user/getShowNewLk'])
  const isUserFetched = computed(() => store.getters['user/isUserFetched'])
  const isUserLogged = computed(() => store.getters['user/isLogged'])

  function checkedSubscription () {
    if (showWarningSubscription()) {
      router.push({ name: 'subscriptions', params: { warning: 'true' } })
    }
  }

  function offline () {
    status.value = 'offline'
  }
  function online () {
    status.value = 'online'
  }
  function logoutHandler () {
    document.addEventListener('logout', () => {
      store.dispatch('logout')
      store.commit('user/setNotice', i18n.global.t('loggedInOtherDevice'))

      const returnUrl: string = typeof router.currentRoute.value.query?.returnUrl === 'string' ? router.currentRoute.value.query?.returnUrl : router.currentRoute.value.fullPath
      store.commit('setReturnUrl', returnUrl)
      router.push({ name: 'login' })
    })
  }

  // Watchers
  const themeWatch = watch(theme, (newTheme, oldTheme) => {
    document.body.classList.remove(`-${oldTheme}`)
    document.body.classList.add(`-${newTheme}`)
  }, {
    immediate: true
  })
  watch(fbqId, id => {
    if (id && window.fbq) {
      window.fbq('init', id)
      window.fbq('track', 'PageView')
    }
  })

  async function initHandler () {
    loading.value = true
    if (!user.value?.id) {
      loading.value = false
      return
    }

    migrateLegacyContinueTrainingKey(user.value.id)

    // Уровень 1 — CRITICAL: subscription отдельным await (нужен для гейта),
    // затем stream. Общий try/catch/finally гарантирует снятие loading
    // и sentry-репорт при любом сбое critical-диспатчей.
    try {
      await store.dispatch('subscription/fetchCachedSubscriptions')
      await store.dispatch('stream/fetchUserCourseStreams')
    } catch (e) {
      sentry.captureException(e)
      Logger.error(e)
    } finally {
      checkedSubscription()
      loading.value = false
    }

    // Уровень 2 — BACKGROUND: fire-and-forget после loading=false
    store.dispatch('community/fetchCachedCommunities').catch(e => {
      Logger.error('community background fetch failed:', e)
    })
    store.dispatch('banner/fetchBanners').catch(e => {
      Logger.error('banner background fetch failed:', e)
    })
    store.dispatch('userScore/amount').catch(e => {
      Logger.error('userScore background fetch failed:', e)
    })

    // Уровень 3 — LAZY: shortCourse/search, installments, userPaymentMethod
    // НЕ диспатчим здесь — они вызываются потребителями (Home, /installments, /user-payment-methods,
    // ClubSupportStep ждёт community/fetchCachedCommunities на onMounted)

    sentryConfigure(user.value, store.getters['user/username'])

    const platform = isShowNewLk.value ? 'influence_lk' : 'standart_lk'
    Analytics.setUserParams({
      typelk: platform,
      userId: user.value.id
    })
    const courseIds = streams.value.map((stream) => stream.courseId)
    const subscriptions = store.getters['subscription/subscriptions']
    const activeSubscriptions = store.getters['subscription/activeSubscriptions']
    Analytics.setSuperSub(subscriptions.some(subscription => superSubscriptionsIds().includes(subscription.id)))
    Analytics.setCountActiveSubscriptions(activeSubscriptions.length)
    if (user.value.isAdmin) {
      Analytics.setTrafficType('internal')
    }
    AnalyticEvents.appOpened(platform)

    const activeSuperSubscription = activeSubscriptions.find(subscription => superSubscriptionsIds().includes(subscription.id))
    if (!isTake.value) {
      yandexMetrika.setUserID(String(user.value.id))
      yandexMetrika.userParams({
        user_email: user.value.email,
        user_id: String(user.value.id),
        typelk: platform,
        courseIds: courseIds.join(','),
        superSubscription: activeSuperSubscription?.isActive || activeSuperSubscription?.isLifetime
      })
    }
  }

  watch(user, (user: User | undefined, oldUser: User | undefined) => {
    if (user?.id && user?.id !== oldUser?.id) {
      initHandler()
    }
  }, {
    deep: true,
    flush: 'sync',
    immediate: true
  })

  watch(hasSubscription, () => {
    checkedSubscription()
  }, {
    deep: true,
    flush: 'sync',
    immediate: true
  })

  watch(streams, () => {
    checkedSubscription()
  }, {
    deep: true,
    flush: 'sync',
    immediate: true
  })

  watch(isUserFetched, (newVal) => {
    if (newVal) {
      const gtmId = getState()?.data?.gtm
      const params: {
        gtmId: string
        userId?: number
        typelk?: AnalyticsTypeLk
      } = {
        gtmId
      }
      if (isUserLogged.value) {
        params.typelk = isShowNewLk.value ? 'influence_lk' : 'standart_lk'
        params.userId = user.value.id
      }
      Analytics.init(params)

      if (!isTake.value) {
        const yaTrackingId = getState()?.data?.yaTrackingId
        if (isShowNewLk.value && yaTrackingId) {
          updateYandexMerikaByConfig({
            id: yaTrackingId,
            router,
            debug: process.env.NODE_ENV !== 'production',
            env: process.env.NODE_ENV,
            scriptSrc: 'https://mc.yandex.ru/metrika/tag.js',
            options: {
              webvisor: true,
              clickmap: true,
              trackLinks: true,
              accurateTrackBounce: true
            }
          })
        }
      }
    }
  }, {
    flush: 'sync',
    immediate: true
  })

  return {
    // local variable
    loading,
    status,

    // store variable
    theme,
    title,
    isLoadedStreams,
    isLoadedSubscriptions,
    user,

    offline,
    online,
    logoutHandler,

    // watchers
    themeWatch,

    initHandler
  }
}

export default useApp
